import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UsersThree, Copy } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { useApi, errText, fmtDate } from "@/lib/api";
import { ActivityFeed } from "@/components/team/ActivityFeed";

const ROLE = { editor: "Editor", viewer: "View-only" };
const STATUS = { active: "text-[#00FF9D]", invited: "text-[#FFD166]", expired: "text-[#FF8FA3]" };

const InviteForm = ({ onDone, full, reset }) => {
  const api = useApi();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setLink(""), [reset]);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/team/invite", { email, role });
      setLink(r.invite_url); setEmail(""); onDone();
      toast.success(r.email_sent ? `Invite sent to ${r.email}` : "Invite created. Email didn't send, so share the link below.");
    } catch (err) { toast.error(errText(err, "Couldn't send the invite")); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} data-testid="team-invite-form" className="rounded-xl border border-[#2A2A2A] bg-[#121212] p-6">
      <h2 className="font-display text-lg">Invite a teammate</h2>
      <div className="mt-4 flex flex-wrap gap-2">
        <input data-testid="team-invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cofounder@company.com"
          className="flex-1 min-w-[220px] rounded-lg bg-[#050505] border border-[#2A2A2A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#00FF9D]" />
        <select data-testid="team-invite-role" value={role} onChange={(e) => setRole(e.target.value)} className="rounded-lg bg-[#050505] border border-[#2A2A2A] px-3 py-2.5 text-sm">
          <option value="editor">Editor</option><option value="viewer">View-only</option>
        </select>
        <button data-testid="team-invite-submit" disabled={busy || full} className="rounded-full bg-[#00FF9D] px-5 py-2.5 text-sm font-semibold text-[#050505] disabled:opacity-40">{busy ? "Sending…" : "Send invite"}</button>
      </div>
      <p className="mt-2 text-[11px] text-[#6B6B6B]">Editors can change forecasts, scenarios, integrations and reports. View-only people can look and ask the Calibrator. Only you handle billing and the team.</p>
      {link && (
        <button type="button" data-testid="team-invite-copy-link" onClick={async () => { await navigator.clipboard.writeText(link); toast.success("Invite link copied"); }}
          className="mt-3 flex items-center gap-2 text-xs text-[#00FF9D] hover:underline"><Copy size={12} /> Copy the invite link (works for 7 days, only for that email)</button>
      )}
    </form>
  );
};

const MemberRow = ({ m, i, owner, refresh, onLink }) => {
  const api = useApi();
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); refresh(); } catch (e) { toast.error(errText(e, "Something went wrong")); } };
  return (
    <div data-testid={`team-member-${i}`} className="flex flex-wrap items-center gap-3 py-3 text-sm">
      <div className="flex-1 min-w-[200px]">
        <p className="truncate">{m.email}</p>
        <p className="font-mono text-[11px] text-[#6B6B6B]"><span className={STATUS[m.status]}>{m.status.toUpperCase()}</span> · {m.status === "active" ? `joined ${fmtDate(m.joined_at)}` : `invited ${fmtDate(m.invited_at)}`}</p>
      </div>
      {owner ? (
        <>
          <select data-testid={`team-member-role-${i}`} value={m.role} onChange={(e) => act(() => api.patch(`/team/members/${m.id}`, { role: e.target.value }), "Role updated")}
            className="rounded-lg bg-[#050505] border border-[#2A2A2A] px-2 py-1.5 text-xs">
            <option value="editor">Editor</option><option value="viewer">View-only</option>
          </select>
          {m.status !== "active" && <button data-testid={`team-member-resend-${i}`} onClick={() => act(async () => { const r = await api.post("/team/invite", { email: m.email, role: m.role }); onLink(r.invite_url); }, "Invite sent again")} className="text-xs text-[#A3A3A3] hover:text-white underline">Resend</button>}
          <button data-testid={`team-member-remove-${i}`} onClick={() => act(() => api.delete(`/team/members/${m.id}`), m.status === "active" ? "Teammate removed" : "Invite cancelled")}
            className="text-xs text-[#FF8FA3] hover:underline">{m.status === "active" ? "Remove" : "Cancel"}</button>
        </>
      ) : <span className="font-mono text-xs text-[#A3A3A3]">{ROLE[m.role]}</span>}
    </div>
  );
};

export default function Team() {
  const api = useApi();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["team"], queryFn: () => api.get("/team") });
  const [reset, setReset] = useState(0);
  const [resentLink, setResentLink] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["team"] });
  const memberChanged = () => { setReset((n) => n + 1); refresh(); };
  if (!data) return null;
  const owner = data.role === "owner";
  const noSeats = owner && data.seats_total === 0;
  const setMfa = async (v) => { try { await api.put("/team/settings", { require_mfa: v }); refresh(); toast.success(v ? "Teammates must use two-step sign-in" : "Two-step sign-in is now optional for teammates"); } catch (e) { toast.error(errText(e, "Couldn't save")); } };
  return (
    <div data-testid="team-page" className="space-y-4 max-w-4xl">
      <p className="font-mono text-xs text-[#A3A3A3]">TEAM</p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1 flex items-center gap-3"><UsersThree className="text-[#00FF9D]" /> Team</h1>
      {owner && data.seats_total === 0 ? (
        <div data-testid="team-upsell" className="rounded-xl border border-[#00FF9D]/30 bg-[#121212] p-6">
          <p className="text-sm">Team seats are part of the Pro plan: invite up to 5 co-founders or finance leads, each with their own login.</p>
          <Link to="/app/billing" data-testid="team-upgrade-link" className="mt-4 inline-block rounded-full bg-[#00FF9D] px-5 py-2.5 text-sm font-semibold text-[#050505]">See Pro</Link>
        </div>
      ) : owner && <InviteForm onDone={refresh} full={data.seats_used >= data.seats_total} reset={reset} />}
      {!(noSeats && data.members.length === 0) && <section className="rounded-xl border border-[#2A2A2A] bg-[#121212] p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-lg">People</h2>
          {owner && <span data-testid="team-seats" className="font-mono text-xs text-[#A3A3A3]">{data.seats_used} of {data.seats_total} seats used</span>}
        </div>
        <p data-testid="team-owner" className="mt-3 text-sm text-[#A3A3A3]">Owner: <span className="text-white">{data.owner.email}</span></p>
        <div className="mt-2 divide-y divide-[#2A2A2A]">
          {data.members.length === 0 ? <p data-testid="team-empty" className="py-3 text-sm text-[#6B6B6B]">No teammates yet.</p>
            : data.members.map((m, i) => <MemberRow key={m.id} m={m} i={i} owner={owner} refresh={memberChanged} onLink={setResentLink} />)}
        </div>
        {resentLink && (
          <button data-testid="team-resend-copy-link" onClick={async () => { await navigator.clipboard.writeText(resentLink); toast.success("New invite link copied"); }}
            className="mt-3 flex items-center gap-2 text-xs text-[#00FF9D] hover:underline"><Copy size={12} /> Copy the new invite link (the old one no longer works)</button>
        )}
      </section>}
      {owner && !noSeats && (
        <section className="rounded-xl border border-[#2A2A2A] bg-[#121212] p-6 flex items-start justify-between gap-6">
          <div>
            <h2 className="font-display text-lg">Require two-step sign-in</h2>
            <p className="mt-1 text-sm text-[#A3A3A3]">Teammates without it will be asked to set it up before they can see anything.</p>
          </div>
          <Switch data-testid="team-require-mfa" checked={data.require_mfa} onCheckedChange={setMfa} />
        </section>
      )}
      <ActivityFeed />
    </div>
  );
}
